<template>
  <div class="complaint-page">
    <main class="complaint-body" v-loading="loading">
      <header class="page-heading"><div><h1>反馈客诉</h1><p>整理玩家信息与问题详情</p></div><el-button size="mini" :disabled="locked || !!success" @click="refresh">刷新</el-button></header>
      <section v-if="success" class="info-card success-state" role="status">
        <el-alert title="已提交至飞书群" type="success" :closable="false" show-icon />
        <p>反馈编号：{{ success.record_no }}</p><p>飞书群：{{ success.group_name }}</p><p>发送身份：{{ success.sender_name }}</p>
        <el-button type="primary" size="small" @click="continueFeedback">继续反馈</el-button>
      </section>
      <template v-else>
      <div v-if="pageError" class="empty-state" role="alert"><i class="el-icon-warning-outline"></i><h2>暂时无法加载</h2><p>{{ pageError }}</p><el-button size="small" @click="refresh">重新加载</el-button></div>
      <template v-else-if="!loading || loaded">
        <div v-if="!customerReady" class="empty-state"><i class="el-icon-chat-dot-round"></i><h2>{{ customerError ? '客户信息暂不可用' : '请从客户会话进入' }}</h2><p>{{ customerMessage || '已完成客服身份授权，请在客户聊天侧边栏打开本页面，以获取当前客户的游戏信息。' }}</p><el-button size="small" @click="refresh">重新获取客户信息</el-button></div>
        <div v-else-if="!bound" class="empty-state"><i class="el-icon-link"></i><h2>当前联系人尚未绑定游戏用户</h2><p>请先完成绑定，再刷新当前页面。</p></div>
        <template v-else>
          <el-alert title="用户已绑定，可选择本次反馈的角色。" type="success" :closable="false" show-icon />
          <section class="type-section"><label class="field-label">客诉类型 <b>*</b></label>
            <el-select v-model="issueType" placeholder="请选择客诉类型" :disabled="locked" @change="clearFields">
              <el-option v-for="item in issueTypes" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </section>
          <template v-if="issueType">
            <h2 class="section-heading">基础信息</h2>
            <section class="info-card">
              <label class="field-label" for="complaint-account">游戏账号</label>
              <form class="account-query" @submit.prevent="queryAccount"><el-input id="complaint-account" v-model="accountInput" placeholder="请输入游戏账号" maxlength="128" :disabled="locked" clearable />
                <el-button native-type="submit" size="small" :disabled="locked || !accountInput.trim()">查询</el-button>
              </form>
              <el-button v-if="searchedAccount" type="text" size="mini" :disabled="locked" @click="resetAccount">返回当前联系人绑定账号</el-button>
              <p v-if="!roles.length" class="inline-empty">{{ searchedAccount ? '该游戏账号未查询到角色，请核对后重试。' : '当前绑定账号暂无角色信息。' }}</p>
              <template v-else>
                <div class="role-picker"><label class="field-label">当前角色 <b>*</b></label>
                  <el-select v-model="roleKey" :disabled="roles.length === 1 || locked" filterable :filter-method="filterRoles" placeholder="选择角色" @change="changeRole">
                    <el-option v-for="role in visibleRoles" :key="role.key" :value="role.key" :label="roleLabel(role)" />
                  </el-select>
                  <p v-if="roles.length > 1" class="hint">支持搜索区服、角色名或角色 ID</p>
                </div>
                <dl v-if="selectedRole" class="facts">
                  <div><dt>平台</dt><dd>{{ selectedRole.platform_name }}</dd></div>
                  <div><dt>游戏 / AppKey</dt><dd>{{ selectedRole.game_name || '—' }} / {{ selectedRole.appkey }}</dd></div>
                  <div><dt>工单后台游戏</dt><dd>{{ roleTarget && roleTarget.worksn_game_name ? roleTarget.worksn_game_name : '未配置' }}</dd></div>
                  <div><dt>用户 ID</dt><dd>{{ selectedRole.user_id }}</dd></div>
                  <div><dt>区服 ID / 名称</dt><dd>{{ selectedRole.server_id || '—' }} / {{ selectedRole.server_name || '—' }}</dd></div>
                  <div><dt>角色名称</dt><dd>{{ selectedRole.role_name || '—' }}</dd></div>
                  <div><dt>角色 ID</dt><dd>{{ selectedRole.role_id }}</dd></div>
                  <div><dt>累计充值</dt><dd>账号 {{ money(selectedRole.user_payment) }}<br />角色 {{ money(selectedRole.role_payment) }}</dd></div>
                </dl>
                <el-alert v-if="selectedRole && worksnGameError" :title="worksnGameError" type="error" :closable="false" show-icon />
              </template>
            </section>
            <template v-if="selectedRole">
              <h2 class="section-heading">{{ currentIssue.label }}信息</h2>
              <section class="info-card scenario-fields">
                <div v-for="field in currentIssue.fields" :key="field.key" class="scenario-field">
                  <label class="field-label" :for="`complaint-${field.key}`">{{ field.label }} <b v-if="field.required">*</b></label>
                  <el-date-picker v-if="field.type === 'date'" :id="`complaint-${field.key}`" v-model="fields[field.key]" type="date" value-format="yyyy-MM-dd" format="yyyy-MM-dd" :placeholder="`请选择${field.label}`" :editable="false" :disabled="locked" />
                  <div v-else-if="field.key === 'bossMap'" class="boss-map-input">
                    <el-input :id="`complaint-${field.key}`" v-model="bossName" placeholder="请输入 BOSS" label="击杀 BOSS" :maxlength="128" :disabled="locked" @input="updateBossMap" />
                    <span class="boss-map-separator" aria-hidden="true">/</span>
                    <el-input id="complaint-mapName" v-model="mapName" placeholder="请输入地图" label="地图" :maxlength="128" :disabled="locked" @input="updateBossMap" />
                  </div>
                  <el-input v-else :id="`complaint-${field.key}`" v-model="fields[field.key]" :type="field.type === 'textarea' ? 'textarea' : 'text'" :rows="4" :maxlength="field.maxlength || 128" :placeholder="`请填写${field.label}`" :disabled="locked"><template v-if="field.type === 'amount'" slot="append">元</template></el-input>
                  <p v-if="field.maxlength" class="hint">最多 {{ field.maxlength }} 个字符</p>
                </div>
              </section>
              <h2 class="section-heading">用户反馈附件</h2>
              <section class="info-card attachment-card"><complaint-attachments ref="attachments" :key="roleKey + ':' + searchedAccount" v-model="attachments" :token="sessionToken" :account="searchedAccount" :role-key="roleKey" :disabled="loading || submitting || !!pendingSubmission || !!worksnGameError" />
                <p class="hint">附件非必填，缺失不阻断。{{ issueType === 'repeat' ? '重复充值建议提供支付截图。' : '' }}</p>
              </section>
              <h2 class="section-heading">提交目标</h2>
              <section class="info-card">
                <el-alert v-if="contextError" :title="contextError" type="error" :closable="false" show-icon />
                <el-alert v-else-if="!targetGroup" title="当前游戏未配置有效飞书群，请联系管理员。" type="warning" :closable="false" show-icon />
                <div v-if="authorizationMessage" class="authorization-help" role="alert">
                  <p>{{ authorizationMessage }}</p>
                  <a v-if="sender && sender.requires_authorization" href="/setting-personal" target="_blank" rel="noopener noreferrer">前往工单后台授权</a>
                  <el-button size="mini" :loading="checkingAuthorization" :disabled="loading || submitting || !!success" @click="checkAuthorization">重新检查</el-button>
                </div>
                <dl class="facts">
                  <div><dt>飞书群</dt><dd>{{ targetGroup ? targetGroup.group_name : '未匹配' }}</dd></div>
                  <div><dt>发送身份</dt><dd>{{ sender ? sender.name : '暂不可用' }}<small v-if="sender">{{ sender.status_label }}</small></dd></div>
                  <div><dt>提醒对象</dt><dd>{{ issueType === 'other' ? '提交时匹配当日值班运营，未匹配时提醒兜底运营' : (targetGroup && targetGroup.bot_name ? '@' + targetGroup.bot_name : '客诉处理机器人') }}</dd></div>
                </dl>
              </section>
            </template>
          </template>
        </template>
      </template>
      <el-alert v-if="submitError" :title="submitError" type="error" :closable="false" show-icon class="submit-error" />
      </template>
    </main>
    <footer v-if="bound && issueType && selectedRole && !pageError && !success" class="submit-bar"><el-button type="primary" :loading="submitting" :disabled="!canSubmit" @click="submit">{{ pendingSubmission ? '重试并确认发送结果' : '提交至飞书群' }}</el-button><span>{{ attachmentMessage || validationMessage || (pendingSubmission ? '原内容已保留，重试使用同一反馈编号' : '请核对以上信息') }}</span></footer>
  </div>
</template>

<script>
import { createComplaintSession, attachComplaintContact, getComplaintPage, getQwSignatureApi, submitComplaint } from '@/api/complaintSidebar';
import ComplaintAttachments from './attachments.vue';
import { loadWecomSdk, registerWecom, getCurrentExternalContact } from '../qwReplyRecommend/wecom';
import { issueTypes, validateScenario } from './schema';

export default {
  name: 'ComplaintFeedback',
  components: { ComplaintAttachments },
  data: () => ({ issueTypes, loading: true, loaded: false, pageError: '', bound: false, sessionToken: '',
    contactUserid: '', customerReady: false, customerMessage: '', customerError: false,
    roles: [], roleKey: '', roleSearch: '', targets: {}, sender: null, contextError: '',
    accountInput: '', searchedAccount: '', issueType: '', fields: {}, bossName: '', mapName: '', requestId: 0, destroyed: false, redirecting: false,
    attachments: [], submitting: false, pendingSubmission: null, submitError: '', success: null,
    checkingAuthorization: false, authorizationError: '' }),
  computed: {
    contextKey() { return `${this.$route.query.corpid || ''}:${this.$route.query.agentid || ''}`; },
    selectedRole() { return this.roles.find(role => role.key === this.roleKey) || null; },
    visibleRoles() { const text = this.roleSearch.trim().toLowerCase(); return this.roles.filter(role => !text || `${this.roleLabel(role)} ${role.server_id || ''} ${role.role_id || ''}`.toLowerCase().includes(text)); },
    currentIssue() { return issueTypes.find(item => item.value === this.issueType) || { fields: [] }; },
    roleTarget() { return this.selectedRole ? this.targets[this.roleKey] || null : null; },
    targetGroup() { return this.roleTarget && this.roleTarget.id ? this.roleTarget : null; },
    worksnGameError() { return this.selectedRole && (!this.roleTarget || !this.roleTarget.worksn_game_id) ? '工单后台未配置对应的合同游戏' : ''; },
    validationMessage() { return validateScenario(this.issueType, this.fields); },
    attachmentMessage() { return this.attachments.some(item => item.status === 'uploading') ? '附件上传中，请稍候' : this.attachments.some(item => item.status !== 'ready') ? '请重试上传失败的附件，或移除后提交' : ''; },
    authorizationMessage() { return this.authorizationError || (!this.sender ? '发送身份尚未确认，请重新检查' : (this.sender.can_submit === true ? '' : this.sender.status_label || '授权状态尚未确认，请重新检查')); },
    locked() { return this.loading || this.submitting || this.checkingAuthorization || !!this.pendingSubmission || this.attachments.some(item => item.status === 'uploading'); },
    canSubmit() { return this.sender && this.sender.can_submit === true && !this.loading && !this.submitting && !this.checkingAuthorization && !this.authorizationMessage && !this.success && !this.pageError && this.customerReady && this.bound && this.selectedRole && this.targetGroup && (!this.worksnGameError || !!this.pendingSubmission) && !this.contextError && !this.validationMessage && !this.attachmentMessage; },
  },
  mounted() { this.initPage(); },
  beforeDestroy() { this.destroyed = true; this.requestId++; },
  watch: {
    contextKey() { this.invalidateContext(); this.initPage(); },
  },
  methods: {
    invalidateContext() { this.clearFields(); this.submitting = false; this.success = null; this.requestId++; this.sessionToken = ''; this.contactUserid = ''; this.customerReady = false; this.customerMessage = ''; this.customerError = false; this.loaded = false; this.bound = false; this.roles = []; this.roleKey = ''; this.targets = {}; this.sender = null; this.fields = {}; this.issueType = ''; this.accountInput = ''; this.searchedAccount = ''; this.contextError = ''; this.redirecting = false; },
    async initPage() {
      const id = ++this.requestId;
      this.loading = true; this.pageError = '';
      const corp = String(this.$route.query.corpid || '').trim();
      const agent = String(this.$route.query.agentid || '1000002');
      try {
        if (!corp) throw new Error('缺少当前企业标识，请从企微侧边栏进入。');
        const code = this.$route.query.code;
        const stateKey = `complaint-oauth-state:${corp}:${agent}`;
        if (!code) { this.redirectAuth(corp, agent, stateKey); return; }
        const expected = window.sessionStorage.getItem(stateKey);
        const validState = expected && expected === this.$route.query.state;
        window.sessionStorage.removeItem(stateKey);
        const query = { ...this.$route.query }; delete query.code; delete query.state;
        await this.$router.replace({ query });
        if (!validState) throw new Error('页面授权校验失败，请重新加载。');
        const session = await createComplaintSession({ corpid: corp, agentid: agent, code });
        if (id !== this.requestId || this.destroyed) return;
        if (!session.session_token || !session.contact_userid) throw new Error('未获取到企微客服身份');
        this.sessionToken = session.session_token;
        this.contactUserid = session.contact_userid;
        await this.loadCustomerContext();
      } catch (error) {
        if (id === this.requestId && !this.destroyed) this.pageError = this.errorText(error);
      } finally { if (id === this.requestId && !this.redirecting && !this.destroyed) this.loading = false; }
    },
    async withSdkTimeout(promise) {
      let timer;
      try {
        return await Promise.race([promise, new Promise((resolve, reject) => {
          timer = window.setTimeout(() => reject(new Error('企微客户信息获取超时，请在客户会话中重试。')), 15000);
        })]);
      } finally { window.clearTimeout(timer); }
    },
    async loadCustomerContext() {
      const id = ++this.requestId;
      this.loading = true; this.pageError = ''; this.customerMessage = ''; this.customerError = false;
      const corp = String(this.$route.query.corpid || '').trim();
      const agent = String(this.$route.query.agentid || '1000002');
      try {
        await this.withSdkTimeout(loadWecomSdk());
        const signature = async (url, ticketType) => {
          const res = await getQwSignatureApi({ corpid: corp, agentid: agent, url, ticket_type: ticketType });
          if (!res || Number(res.ret) !== 1 || !res.content) throw new Error('企微签名获取失败，请稍后刷新重试。');
          return { timestamp: res.content.timestamp, nonceStr: res.content.nonce_str, signature: res.content.signature };
        };
        await this.withSdkTimeout(registerWecom({ corpId: corp, agentId: agent,
          getConfigSignature: url => signature(url, 'jsapi'),
          getAgentConfigSignature: url => signature(url, 'agent_config'),
        }));
        const external = await this.withSdkTimeout(getCurrentExternalContact());
        if (!external) throw new Error('未获取到当前客户，请从客户会话侧边栏进入。');
        if (id !== this.requestId || this.destroyed) return;
        const session = await attachComplaintContact(this.sessionToken, external);
        if (id !== this.requestId || this.destroyed) return;
        if (!session.session_token) throw new Error('未获取到客户会话授权，请刷新重试。');
        this.sessionToken = session.session_token; this.customerReady = true;
        await this.loadPage('');
      } catch (error) {
        if (id !== this.requestId || this.destroyed) return;
        this.customerReady = false;
        if (error && error.response && error.response.status === 403) {
          this.customerError = true; this.customerMessage = this.errorText(error);
          return;
        }
        if (error && error.response && error.response.status === 401) {
          this.sessionToken = ''; this.contactUserid = '';
          this.pageError = this.errorText(error);
          return;
        }
        this.customerMessage = '客服身份已确认，当前未获取到客户信息。请从客户会话侧边栏进入，或刷新重试。';
        if (error && error.message) this.customerMessage += `（${this.errorText(error)}）`;
      } finally { if (id === this.requestId && !this.destroyed) this.loading = false; }
    },
    redirectAuth(corp, agent, key) {
      const random = new Uint32Array(4); window.crypto.getRandomValues(random);
      const state = 'complaint_' + Array.from(random).map(value => value.toString(16)).join('');
      window.sessionStorage.setItem(key, state);
      const redirect = new URL(window.location.href); redirect.search = ''; redirect.hash = '';
      redirect.searchParams.set('corpid', corp); redirect.searchParams.set('agentid', agent);
      this.redirecting = true;
      window.location.replace(`https://open.weixin.qq.com/connect/oauth2/authorize?appid=${encodeURIComponent(corp)}&redirect_uri=${encodeURIComponent(redirect.toString())}&response_type=code&scope=snsapi_base&agentid=${encodeURIComponent(agent)}&state=${state}#wechat_redirect`);
    },
    async loadPage(account) {
      const id = ++this.requestId; this.loading = true; this.pageError = '';
      try {
        const data = await getComplaintPage(this.sessionToken, account);
        if (id !== this.requestId || this.destroyed) return;
        const oldRole = this.roleKey;
        const sameAccount = account === this.searchedAccount;
        this.bound = data.bound === true; this.roles = Array.isArray(data.roles) ? data.roles : [];
        this.targets = data.targets || {}; this.sender = data.sender || null; this.contextError = data.context_error || ''; this.authorizationError = '';
        this.roleKey = sameAccount && this.roles.some(role => role.key === oldRole) ? oldRole : (this.roles[0] ? this.roles[0].key : '');
        this.searchedAccount = account;
        this.accountInput = account || (this.selectedRole ? this.selectedRole.user_name : '');
        this.roleSearch = ''; this.loaded = true;
        if (!sameAccount || oldRole !== this.roleKey) this.clearFields();
      } catch (error) {
        if (id !== this.requestId || this.destroyed) return;
        // 查询失败保留输入，同时阻断旧快照的提交，避免把旧账号当成新查询结果。
        this.pageError = this.errorText(error);
        if (error && error.response && error.response.status === 401) { this.sessionToken = ''; this.contactUserid = ''; this.customerReady = false; }
      } finally { if (id === this.requestId && !this.destroyed) this.loading = false; }
    },
    async checkAuthorization() {
      if (this.loading || this.submitting || this.checkingAuthorization || this.success) return;
      const context = this.requestId;
      this.checkingAuthorization = true; this.authorizationError = '';
      try {
        const data = await getComplaintPage(this.sessionToken, this.searchedAccount);
        if (this.destroyed || context !== this.requestId) return;
        if (!data.sender || data.context_error) throw new Error(data.context_error || '授权检查暂时失败，请稍后重试');
        // 只更新授权状态，保留失败提交的原编号、表单、附件与当前角色。
        this.sender = data.sender;
        if (this.sender.can_submit === true) this.submitError = '';
      } catch (error) {
        if (!this.destroyed && context === this.requestId) this.authorizationError = this.errorText(error);
      } finally { this.checkingAuthorization = false; }
    },
    refresh() { if (this.locked || this.success) return; return this.sessionToken ? (this.customerReady ? this.loadPage(this.searchedAccount) : this.loadCustomerContext()) : this.initPage(); },
    queryAccount() { const account = this.accountInput.trim(); if (this.locked || !account) return; return this.loadPage(account); },
    resetAccount() { if (!this.locked) return this.loadPage(''); },
    changeRole() { this.clearFields(); this.roleSearch = ''; },
    clearFields() { this.fields = {}; this.bossName = ''; this.mapName = ''; this.pendingSubmission = null; this.submitError = ''; this.attachments = []; if (this.$refs.attachments) this.$refs.attachments.clear(); },
    updateBossMap() { const boss = this.bossName.trim(), map = this.mapName.trim(); this.$set(this.fields, 'bossMap', boss || map ? `${boss} / ${map}` : ''); },
    filterRoles(text) { this.roleSearch = text; },
    roleLabel(role) { return `${role.server_name || '未知区服'} · ${role.role_name || '未命名角色'}`; },
    money(value) { return value === null || value === undefined || value === '' ? '—' : `¥${value}`; },
    async submit() {
      if (!this.canSubmit) return;
      if (!this.pendingSubmission) {
        const random = new Uint32Array(4); window.crypto.getRandomValues(random);
        this.pendingSubmission = { request_id: Array.from(random).map(value => value.toString(16).padStart(8, '0')).join(''),
          account: this.searchedAccount, role_key: this.roleKey, issue_type: this.issueType, fields: { ...this.fields },
          attachment_ids: this.attachments.map(item => item.id) };
      }
      const context = this.requestId; const payload = this.pendingSubmission;
      this.submitting = true; this.submitError = '';
      try {
        const result = await submitComplaint(this.sessionToken, payload);
        if (this.destroyed || context !== this.requestId) return;
        if (Number(result.send_status) === 1) { this.success = result; return; }
        this.submitError = result.failure_reason || (Number(result.send_status) === 3 ? '正在发送，请稍后点击重试确认结果' : '发送未完成，请保留当前内容重试');
        if (['authorization_required', 'authorization_check_failed'].includes(result.failure_code)) {
          this.sender = { ...this.sender, type: 'user', can_submit: false,
            requires_authorization: result.failure_code === 'authorization_required', status_label: this.submitError };
        }
      } catch (error) {
        if (!this.destroyed && context === this.requestId) {
          this.submitError = this.errorText(error);
          // 参数拒绝也可能发生于既有反馈的重试；始终保留原幂等编号，避免绕过服务端去重。
        }
      } finally { if (!this.destroyed && context === this.requestId) this.submitting = false; }
    },
    continueFeedback() { this.success = null; this.clearFields(); this.issueType = ''; },
    errorText(error) { return (error && error.response && error.response.data && (error.response.data.msg || error.response.data.message)) || (error && error.message) || '页面信息查询失败，请重试。'; },
  },
};
</script>

<style scoped>
.complaint-page { height: 100%; overflow-y: auto; overflow-x: hidden; background: #f5f7fa; color: #25314c; font-size: 13px; }
.complaint-body { padding: 18px 14px 116px; max-width: 680px; margin: 0 auto; min-height: calc(100vh - 162px); }
.page-heading { display: flex; align-items: center; justify-content: space-between; margin: 0 0 16px; }
.page-heading h1 { margin: 0; font-size: 19px; line-height: 1.5; }
.page-heading p { color: #8791a3; margin: 4px 0 0; font-size: 12px; }
.type-section { margin-top: 18px; padding: 14px; background: #fff; border: 1px solid #e5e9f0; border-radius: 7px; }
.field-label { display: block; margin-bottom: 8px; color: #58657a; font-size: 12px; }
.field-label b { color: #e05c63; font-weight: 400; }
.section-heading { font-size: 13px; font-weight: 600; margin: 21px 0 9px; }
.info-card { background: #fff; border: 1px solid #e5e9f0; border-radius: 7px; padding: 14px; }
.account-query { display: flex; gap: 8px; margin-bottom: 4px; }
.account-query .el-input { flex: 1; min-width: 0; }
.account-query .el-button { padding: 0 13px; }
.role-picker { margin-top: 16px; }
.el-select, .el-date-editor.el-input { width: 100%; }
.facts { margin: 5px 0 0; }
.facts > div { display: flex; gap: 12px; padding: 11px 0; border-bottom: 1px solid #f0f2f6; line-height: 1.65; }
.facts > div:last-child { border: 0; padding-bottom: 0; }
.facts dt { color: #8791a3; width: 94px; flex-shrink: 0; font-size: 12px; }
.facts dd { margin: 0; flex: 1; min-width: 0; overflow-wrap: anywhere; }
.facts small { display: block; color: #8791a3; font-size: 11px; }
.authorization-help { margin-top: 12px; color: #b45f20; font-size: 12px; line-height: 1.7; }
.authorization-help p { margin: 0 0 8px; }
.authorization-help a { display: inline-block; color: #3370ff; margin: 0 12px 8px 0; }
.scenario-field + .scenario-field { margin-top: 16px; }
.boss-map-input { display: flex; align-items: center; gap: 8px; }
.boss-map-input .el-input { flex: 1; min-width: 0; }
.boss-map-separator { flex-shrink: 0; color: #58657a; }
.hint, .inline-empty { color: #8791a3; font-size: 11px; line-height: 1.7; margin: 7px 0 0; }
.upload-placeholder { display: flex; flex-direction: column; justify-content: center; width: 140px; height: 76px; border: 1px dashed #ccd4e2; color: #64748b; }
.upload-placeholder i { display: block; font-size: 22px; margin-bottom: 8px; }
.submit-bar { position: fixed; bottom: 0; left: 0; right: 0; border-top: 1px solid #e5e9f0; background: rgba(255,255,255,.98); padding: 13px 14px 10px; z-index: 10; }
.submit-bar .el-button { display: block; width: 100%; max-width: 680px; margin: 0 auto; background: #3370ff; border-color: #3370ff; }
.submit-bar .el-button.is-disabled { background: #a5bef8; border-color: #a5bef8; }
.submit-bar > span { display: block; margin-top: 6px; font-size: 11px; color: #8791a3; text-align: center; }
.empty-state { padding: 64px 12px; text-align: center; color: #8791a3; }
.empty-state > i { font-size: 34px; color: #a9b7ce; }
.empty-state h2 { font-size: 15px; color: #58657a; margin: 18px 0 9px; }
.empty-state p { font-size: 12px; line-height: 1.8; margin: 0 0 18px; }
@media (max-width: 360px) { .complaint-body { padding-left: 10px; padding-right: 10px; } .info-card { padding: 12px; } .facts dt { width: 86px; } }
</style>

<style>
.complaint-placeholder-message { min-width: 0; width: calc(100vw - 24px); max-width: 380px; box-sizing: border-box; }
</style>
